import React, { useEffect, useMemo, useRef, useState } from "react";

type ChipGroup = {
  label: string;
  options: string[];
};

type ChipMultiSelectProps = {
  groups?: ChipGroup[];
  options?: string[];
  value: string[];
  onChange: (value: string[]) => void;
  placeholder?: string;
  searchPlaceholder?: string;
  maxSelected?: number;
  allowCustom?: boolean;
  customLabel?: string;
  onLimitHit?: (message: string) => void;
};

const flattenGroups = (groups: ChipGroup[]) =>
  groups.flatMap((group) => group.options.map((option) => ({ group: group.label, option })));

export default function ChipMultiSelect({
  groups,
  options,
  value,
  onChange,
  placeholder = "Select options",
  searchPlaceholder = "Search...",
  maxSelected,
  allowCustom = false,
  customLabel = "Add custom",
  onLimitHit,
}: ChipMultiSelectProps) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const rootRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const handleOutside = (event: MouseEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handleOutside);
    return () => document.removeEventListener("mousedown", handleOutside);
  }, []);

  const groupList = useMemo<ChipGroup[]>(() => {
    if (groups?.length) return groups;
    return [{ label: "Options", options: options ?? [] }];
  }, [groups, options]);

  const allOptions = useMemo(() => {
    const seen = new Set<string>();
    return flattenGroups(groupList)
      .filter(({ option }) => {
        if (seen.has(option)) return false;
        seen.add(option);
        return true;
      })
      .map(({ option }) => option);
  }, [groupList]);

  const filteredGroups = useMemo(() => {
    const term = query.trim().toLowerCase();
    return groupList
      .map((group) => ({
        ...group,
        options: group.options.filter((option) => option.toLowerCase().includes(term)),
      }))
      .filter((group) => group.options.length > 0);
  }, [groupList, query]);

  const canAddCustom =
    allowCustom &&
    query.trim().length > 0 &&
    !allOptions.some((option) => option.toLowerCase() === query.trim().toLowerCase()) &&
    !value.some((option) => option.toLowerCase() === query.trim().toLowerCase());

  const toggleOption = (option: string) => {
    if (value.includes(option)) {
      onChange(value.filter((item) => item !== option));
      return;
    }

    if (maxSelected && value.length >= maxSelected) {
      onLimitHit?.(`You can choose up to ${maxSelected} items.`);
      return;
    }

    onChange([...value, option]);
  };

  return (
    <div ref={rootRef} className="relative">
      <button
        type="button"
        onClick={() => setOpen((current) => !current)}
        className="flex min-h-[44px] w-full items-center justify-between rounded-xl border border-[#D6DADF] bg-white px-3 py-2 text-left text-base text-[#111827] shadow-sm"
      >
        <span className={value.length ? "" : "text-[#9CA3AF]"}>
          {value.length ? `${value.length} selected` : placeholder}
        </span>
        <span className="material-icons text-[20px] text-[#6B7280]">expand_more</span>
      </button>

      {value.length > 0 && (
        <div className="mt-3 flex flex-wrap gap-2">
          {value.map((item) => (
            <button
              key={item}
              type="button"
              onClick={() => toggleOption(item)}
              className="inline-flex min-h-[36px] items-center gap-2 rounded-full border border-[#BFE4C5] bg-[#EEF9EE] px-3 py-1 text-sm text-[#166534]"
            >
              <span>{item}</span>
              <span className="material-icons text-[16px]">close</span>
            </button>
          ))}
        </div>
      )}

      {open && (
        <div className="absolute left-0 right-0 top-[calc(100%+8px)] z-50 overflow-hidden rounded-2xl border border-[#E5E7EB] bg-white shadow-xl">
          <div className="border-b border-[#F1F5F9] p-3">
            <input
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder={searchPlaceholder}
              className="h-11 w-full rounded-xl border border-[#D6DADF] px-3 text-base outline-none focus:border-[#93C893]"
            />
          </div>
          <div className="max-h-72 overflow-y-auto p-3">
            {filteredGroups.length === 0 && !canAddCustom && (
              <div className="py-4 text-sm text-[#6B7280]">No options found.</div>
            )}
            {filteredGroups.map((group) => (
              <div key={group.label} className="mb-4 last:mb-0">
                {groups?.length ? (
                  <div className="mb-2 text-xs font-semibold uppercase tracking-[0.08em] text-[#6B7280]">
                    {group.label}
                  </div>
                ) : null}
                <div className="flex flex-wrap gap-2">
                  {group.options.map((option) => {
                    const active = value.includes(option);
                    return (
                      <button
                        key={option}
                        type="button"
                        onClick={() => toggleOption(option)}
                        className={`min-h-[40px] rounded-full border px-3 py-2 text-sm transition ${
                          active
                            ? "border-[#93C893] bg-[#EEF9EE] text-[#166534]"
                            : "border-[#E5E7EB] bg-white text-[#1F2937]"
                        }`}
                      >
                        {option}
                      </button>
                    );
                  })}
                </div>
              </div>
            ))}

            {canAddCustom && (
              <button
                type="button"
                onClick={() => {
                  toggleOption(query.trim());
                  setQuery("");
                }}
                className="mt-2 inline-flex min-h-[40px] items-center gap-2 rounded-full border border-dashed border-[#93C893] px-3 py-2 text-sm text-[#166534]"
              >
                <span className="material-icons text-[18px]">add</span>
                <span>
                  {customLabel}: {query.trim()}
                </span>
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
